import React, { useState } from "react";
import { TitleBlock, Field, Input, SelectInput, Note, WarnNote, Actions, PrimaryBtn, TextBtn } from "../../../../auth/v2/pieces";
import { ScoreCard, Section, RowList, OptionCard, OutlineBadge, visibilityRead, visibilityUnread, visibilityHalf, confidenceLabel, UNREAD_COPY, EMPTY } from "../onboardingPieces";
import { usePhoneTheme } from "../../../../ForBrands/useIsPhone";
import { fmtIndex10 } from "../../../../../utils/indexScore";
import chevronRight from "../../../../../assets/brand/onboarding/chevron-right-9x15.svg";

// 10 · Add your game data (link a source, or type season numbers).
export const PerformanceChoiceStep = ({ state, onImport, onManual, onSkip }) => {
  const [mode, setMode] = useState("link");
  const [link, setLink] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const teamName = state.team?.teamName || "Your team";
  // A rostered team (league and team on file) has data, just none for this athlete yet.
  const rostered = !!(state.team?.league && state.team?.teamId);
  const submit = async () => {
    if (mode === "manual") { onManual(); return; }
    if (!/^(https?:\/\/)?[\w.-]+\.[a-z]{2,}/i.test(link.trim())) { setError("Paste the full link to your profile."); return; }
    setBusy(true); setError("");
    try { await onImport(link.trim()); } catch (e) { setError(e.message || "We could not save that link."); } finally { setBusy(false); }
  };
  return (
    <>
      <TitleBlock eyebrow="Performance · required" title="Add your game data" sub={rostered ? `We have no game stats for you from ${teamName} yet. Supply them and your performance score calculates.` : `${teamName} is not in our roster data, so we cannot pull your stats. Supply them and your performance score calculates.`} />
      <div className="flex flex-col gap-[10px]">
        <OptionCard className="min-h-[150px]" selected={mode === "link"} onClick={() => setMode("link")} title="Link your profile using external source" sub="Passport App, MaxPreps, Hudl, SportsRecruits. We add it to your profile" badge="Recommended" />
        <OptionCard className="min-h-[110px]" selected={mode === "manual"} onClick={() => setMode("manual")} title="Enter my season stats" sub="Type in your key numbers for this season" />
      </div>
      {mode === "link" && (
        <Field label="Profile link">
          <Input placeholder="maxpreps.com/…" value={link} onChange={(e) => setLink(e.target.value)} inputMode="url" autoCapitalize="none" autoCorrect="off" />
        </Field>
      )}
      <Note>Skipping this leaves your performance half empty. Your visibility score is unaffected.</Note>
      {error && <Note tone="critical">{error}</Note>}
      <Actions>
        <PrimaryBtn onClick={submit} loading={busy}>{mode === "link" ? "Add my link" : "Enter my stats"}</PrimaryBtn>
        <TextBtn onClick={onSkip}>Skip for now</TextBtn>
      </Actions>
    </>
  );
};

const seasons = () => {
  const y = new Date().getFullYear();
  const start = new Date().getMonth() >= 6 ? y : y - 1;
  return [0, 1, 2].map((i) => `${start - i}–${String(start - i + 1).slice(2)}`);
};

// 10a · Your season numbers. Phones (1678:1272): the stat fields sit in a
// two-column grid that shares the row (two fixed 166px fields overflow a
// 360px screen).
export const SeasonStatsStep = ({ onSave }) => {
  const phone = usePhoneTheme();
  const [form, setForm] = useState({ season: seasons()[0], pointsPerGame: "", reboundsPerGame: "", assistsPerGame: "", gamesPlayed: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    const nums = ["pointsPerGame", "reboundsPerGame", "assistsPerGame", "gamesPlayed"].filter((k) => form[k] !== "");
    if (nums.length === 0) { setError("Enter at least one number."); return; }
    if (nums.some((k) => Number.isNaN(Number(form[k])) || Number(form[k]) < 0)) { setError("Numbers only."); return; }
    setBusy(true); setError("");
    try {
      const stats = { season: form.season };
      nums.forEach((k) => { stats[k] = Number(form[k]); });
      await onSave(stats);
    } catch (e) { setError(e.message || "We could not save your stats."); } finally { setBusy(false); }
  };
  const field = (k, label, ph) => (
    <Field label={label}><Input className={phone ? "" : "!w-[166px]"} inputMode="decimal" placeholder={ph} value={form[k]} onChange={set(k)} /></Field>
  );
  const pair = phone ? "grid grid-cols-2 gap-[10px]" : "flex gap-[10px]";
  return (
    <>
      <TitleBlock eyebrow="Performance · manual" title="Your season numbers" sub="Enter per game stats. More fields mean a more accurate performance score." />
      <Field label="Season">
        <SelectInput value={form.season} onChange={set("season")}>{seasons().map((s) => <option key={s} value={s}>{s}</option>)}</SelectInput>
      </Field>
      <div className={pair}>{field("pointsPerGame", "Points per game", "24.2")}{field("reboundsPerGame", "Rebounds per game", "6.8")}</div>
      <div className={pair}>{field("assistsPerGame", "Assists per game", "5.1")}{field("gamesPlayed", "Games played", "18")}</div>
      <WarnNote>Self-reported stats show an unverified badge until a coach confirms them.</WarnNote>
      {error && <Note tone="critical">{error}</Note>}
      <Actions><PrimaryBtn onClick={save} loading={busy}>Save my stats</PrimaryBtn></Actions>
    </>
  );
};

// 11 · Performance calculated. Phones (1676:1150): the compact card with the
// confidence badge and the halves card; the rows stay the web's (D-o9).
export const PerformanceDoneStep = ({ state, onNext }) => {
  const p = state.performance || {};
  // A linked account counts only once it was read (see visibilityRead).
  const vis = visibilityRead(state);
  const unread = visibilityUnread(state);
  return (
    <>
      <ScoreCard
        score={state.metrics?.cacheScore || 0}
        title={vis ? "Your CacheScore is live" : "Partial score"}
        sub={vis ? "Both halves are in, visibility from your socials, performance from your team." : unread ? `Performance is in. ${UNREAD_COPY}` : "Performance is in. Connect a social account any time to add visibility."}
        confidence={confidenceLabel(state)}
        visibility={visibilityHalf(state)}
        performance={{ state: "on", note: p.source === "manual" ? "Self-reported" : "From team data" }}
      />
      <Section label={p.source === "manual" ? "Your season numbers" : "From your team data"}>
        <RowList rows={[
          { title: "Games played", sub: p.season ? `${p.season} season` : "This season", value: p.gamesPlayed != null ? String(p.gamesPlayed) : EMPTY },
          { title: "Points per game", sub: p.source === "manual" ? "Unverified until a coach confirms" : "Live from the roster feed", value: p.pointsPerGame != null ? Number(p.pointsPerGame).toFixed(1) : EMPTY },
          // The pillar out of 10 (utils/indexScore); it is stored out of 100.
          { title: "Performance score", sub: "Out of 10", value: state.metrics?.performance != null ? fmtIndex10(state.metrics.performance) : EMPTY },
        ]} />
      </Section>
      <Note wide>{vis ? "Both halves are in. Your CacheScore is live and brands can find you." : unread ? `${UNREAD_COPY} Public accounts read best.` : "Add a social account later and the visibility half fills in immediately."}</Note>
      <Actions><PrimaryBtn onClick={onNext}>See my CacheScore</PrimaryBtn></Actions>
    </>
  );
};

// Phones (1693:1396): 16px radius, 149px tall, a grey dot on the badge, and
// the 9x15 chevron (1693:1403) where the web draws a "›".
const WayCard = ({ title, sub, badge, onClick }) => {
  const phone = usePhoneTheme();
  return (
    <button type="button" onClick={onClick} className={phone ? "flex min-h-[149px] w-full items-center justify-between gap-[14px] rounded-[16px] border border-bm-border bg-white px-[18px] py-4 text-left hover:border-bm-strong" : "flex w-full items-center justify-between gap-4 rounded-[12px] border border-bm-border bg-white p-[18px] text-left hover:border-bm-strong"}>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="bm-h-s text-bm-ink">{title}</span>
        <span className="bm-body-m text-bm-muted">{sub}</span>
        <span className="mt-1"><OutlineBadge dot={phone ? "grey" : "blue"}>{badge}</OutlineBadge></span>
      </span>
      {phone ? (
        <img src={chevronRight} alt="" className="h-[15px] w-[9px] shrink-0" />
      ) : (
        <span className="bm-body-l text-bm-tertiary">›</span>
      )}
    </button>
  );
};

// 11a · No data yet. Phones (1580:1038): the compact card, muted, with "No
// Confidence score", and the halves card.
export const NoDataStep = ({ state, onStats, onSocial, onNext }) => {
  const phone = usePhoneTheme();
  return (
    <>
      <ScoreCard
        chip="No data yet"
        chipDot="blue"
        muted
        highlight={false}
        title="No score yet"
        sub="Connect a social account or add a verified team and the score starts building."
        confidence="No Confidence score"
        visibility={{ state: "warn", note: "Not connected" }}
        performance={{ state: "warn", note: "Awaiting stats" }}
      />
      <div className={phone ? "flex flex-col gap-2 rounded-[16px] bg-bm-subtle p-[18px]" : "flex flex-col gap-[10px] rounded-[16px] bg-bm-subtle px-[18px] py-4"}>
        <span className="bm-overline">Why there is no score</span>
        <p className="bm-body-m text-bm-muted">You added your team manually, so we have no game data yet, and no social account is connected, so there is no visibility data either. Supply either one and the score starts.</p>
      </div>
      <Section label="Two ways to start it">
        <div className="flex flex-col gap-[10px]">
          <WayCard title="Add your game stats" sub="Link a passport profile or type your season numbers" badge="Performance half" onClick={onStats} />
          <WayCard title="Connect a social account" sub="We import reach and engagement automatically" badge="Visibility half" onClick={onSocial} />
        </div>
      </Section>
      <Actions>
        <PrimaryBtn onClick={onStats}>Add my game stats</PrimaryBtn>
        <TextBtn onClick={onSocial}>Connect a social account</TextBtn>
        <button type="button" onClick={onNext} className="mx-auto bm-body-s text-bm-tertiary hover:text-bm-muted">Continue for now</button>
      </Actions>
    </>
  );
};
